import axios from '@/lib/axios'
import { useGlobalErrorsStore } from '@/stores/globalErrors'
import { useHotelsStore } from '@/stores/hotels'
import { useQuery } from '@tanstack/vue-query'
import { error } from 'node:console'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ToastInterface, useToast } from 'vue-toastification'

import useInvalidateQuery from '@/composables/useInvalidateQueryData'

import zoomByCategory from '../helpers/zoomByCategory'
import { Hotel } from '../interfaces/hotel'
import type { HotelListResponse } from '../interfaces/hotelListResponse'
import type { HotelsSearchRequest } from '../interfaces/hotelsSearchRequest'
import useHotelFormValidation from './useHotelFormValidation'

const getHotels = async (
	payload: HotelsSearchRequest,
	toast: ToastInterface,
	setErrorMessage: (message: string) => void = () => {},
	setGlobalError: (message: string, timezone?: string) => void = () => {},
	keepDataOnError: boolean = false
): Promise<HotelListResponse | void> => {
	try {
		const { data } = await axios.post<HotelListResponse>(
			'/api/hotels/search',
			payload
		)

		setErrorMessage('')
		setGlobalError('')

		return data
	} catch (err: any) {
		const messageUser = err?.response?.data?.errors?.users ?? undefined

		if (messageUser) {
			// El 422 trae "users" (mensaje general) y "users.N" (el conflicto del
			// viajero N) como strings planos, pero TravelerConflictAlert espera
			// {message, travelerId} para pintar el detalle y poder quitar al
			// viajero — sin esto la píldora roja sale vacía. Se enriquece cada
			// users.N con el id que se mandó en el payload de búsqueda.
			const rawErrors = err.response.data.errors as Record<string, string[]>
			const errors: Record<string, unknown[]> = {}

			Object.entries(rawErrors).forEach(([key, messages]) => {
				const match = /^users\.(\d+)$/.exec(key)

				errors[key] =
					match ?
						messages.map((message) => ({
							message,
							travelerId: Number(payload.users[Number(match[1])]),
						}))
					:	messages
			})

			const enriched: any = { ...err.response.data, errors }
			setErrorMessage(enriched)
		} else {
			setGlobalError(
				err.response.data.message || 'Error al buscar hoteles',
				err.response.data.timezone
			)
			toast.error(err.response.data.message || 'Error al buscar hoteles')
		}

		// Si keepDataOnError es true, no lanzar el error para mantener los datos previos
		if (!keepDataOnError) {
			throw err
		}
	}
}

const useHotels = () => {
	const {
		form,
		selectedHotel,
		hotels,
		filteredHotelList,
		center,
		zoom,
		reservationNights,
		searchQuery,
		moreIndicator,
		searchId,
		selectedUsers,
		policyErrorFlag,
		policyInformation,
		errorMessage,
		orderBy,
		favoritePriceOrder,
	} = storeToRefs(useHotelsStore())
	const {
		setHotels,
		setMoreIndicator,
		setShowProviderBadge,
		setHotelPreferences,
		setSearchContext,
		setSearchId,
		setForm,
		setFormValue,
		setFormValues,
		setCenter,
		setZoom,
		setSelectedHotel,
		setSelectedUsers,
		setOrderBy,
		setFavoritePriceOrder,
		resetFilters,
		resetValues,
		setSearchQuery,
		setPolicyInformation,
		resetPolicyInformation,
		setErrorMessage,
	} = useHotelsStore()

	const { hotelVuelitate, raiseErrors } = useHotelFormValidation()
	const router = useRouter()
	const toast = useToast()
	const { invalidateQuery } = useInvalidateQuery()
	const { setError } = useGlobalErrorsStore()

	const queryKey = computed(() => [
		'hotelSearch',
		...(Object.values(form.value) as any[]),
	])

	const { data, isFetched, isFetching, isError, error, refetch, remove } =
		useQuery(
			queryKey.value,
			() => getHotels(form.value, toast, setErrorMessage, setError),
			{
				retry: false,
				enabled: false,
				cacheTime: 60000,
			}
		)

	const pushToDetails = (hotel: Hotel) => {
		const queryParams = {
			...form.value,
			IsPerRoom: Number(form.value.IsPerRoom),
			HotelCode: hotel.id,
			// El detalle rutea por proveedor (Hotelbeds → detalle rico normalizado).
			// Amadeus omite `provider` y cae al flujo intacto.
			provider: hotel.provider,
			users: JSON.stringify(form.value.users),
			hotelName: hotel.name,
			previousRequest: form.value.previousRequest,
			previousRequestData: JSON.stringify(form.value.previousRequestData),
			// El detalle abierto desde la lista es parte de esta búsqueda, no una nueva:
			// lleva su id hasta la reserva para marcarla «Con reserva». El destino de la
			// búsqueda (place_id, etiqueta) no le sirve al detalle de un hotel.
			hotelSearchId: searchId.value ?? undefined,
			placeId: undefined,
			destinationLabel: undefined,
		}

		const detailsUrl = router.resolve({
			name: 'hotel.details',
			query: { ...queryParams },
		})
		window.open(detailsUrl.href, '_blank')
	}

	const searchHotels = async () => {
		// Lo de la búsqueda anterior se limpia ANTES de buscar. Al final (como estaba)
		// borraba lo que acaba de contestar esta: desde que `refetch()` se espera, el
		// watch de `data` ya pintó las violaciones de política para entonces, y el
		// formulario se reabría sin decir por qué.
		resetPolicyInformation()
		invalidateQuery(['hotelSearch'])
		if (!hotelVuelitate.value.$invalid) {
			resetFilters()
			await refetch()

			setZoom(zoomByCategory(form.value.destinationCategory))
		} else {
			raiseErrors()
		}
	}

	watch(
		data,
		(value) => {
			const { errors, warnings } = value || {}

			setPolicyInformation({
				errors,
				warnings,
			})

			// Una instancia nueva (resumen/mapa) todavía no tiene respuesta propia.
			// Su watcher inmediato no debe borrar el contexto de la búsqueda visible.
			if (value) {
				setSearchId(value.searchId ?? null)
				setSearchContext(value.searchContext ?? null)
				if (value.searchContext) {
					setFormValues({
						latitude: value.searchContext.latitude ?? form.value.latitude,
						longitude: value.searchContext.longitude ?? form.value.longitude,
						companyBranchId: value.searchContext.companyBranchId ?? undefined,
					})
				}
			}

			if (value?.hotels) {
				setSearchQuery('')
				setHotels(value.hotels)
				setMoreIndicator(value.moreIndicator)
				// Ausente = oculto (p. ej. respuesta de Amadeus puro, que no trae la llave).
				setShowProviderBadge(value.showProviderBadge === true)
				setHotelPreferences(value.hotelPreferences ?? null)

				setCenter({
					lat: form.value.latitude,
					lng: form.value.longitude,
				})
			}

			if (value?.message || value?.errors?.length) {
				setHotels([])
				setMoreIndicator('')
			}
		},
		{
			immediate: true,
			deep: true,
		}
	)

	onBeforeUnmount(() => {
		remove()
	})

	return {
		form,
		hotels,
		selectedHotel,
		selectedUsers,
		center,
		zoom,
		reservationNights,
		searchQuery,
		isFetching,
		isFetched,
		moreIndicator,
		isError,
		error,
		hotelVuelitate,
		policyErrorFlag,
		filteredList: filteredHotelList,
		policyInformation,
		orderBy,
		favoritePriceOrder,

		setForm,
		setFormValue,
		setFormValues,
		setCenter,
		setSelectedHotel,
		setSelectedUsers,
		setOrderBy,
		setFavoritePriceOrder,
		resetFilters,
		resetValues,
		pushToDetails,
		searchHotels,
		data,
		errorMessage,
		refetch,
	}
}

export default useHotels
